'use client';
import React, { useState } from 'react';
import PublicNav from '@/components/PublicNav';
import HomeFooter from '@/app/components/HomeFooter';
import { ChevronDown, HelpCircle, MessageCircle } from 'lucide-react';
import Link from 'next/link';

const faqCategories = [
  {
    id: 'general',
    label: 'General',
    faqs: [
      { id: 'g1', q: 'Who can participate in Motion Max Media Awards?', a: 'Any Indian citizen or resident above 16 years of age can participate. We welcome filmmakers, photographers, content creators, and digital storytellers at all experience levels — from first-time creators to seasoned professionals.' },
      { id: 'g2', q: 'Is there an age limit for participation?', a: 'Participants must be at least 16 years old. Contestants below 18 years require written consent from a parent or legal guardian, which must be submitted along with the entry.' },
      { id: 'g3', q: 'Can I submit entries from outside India?', a: 'Currently, Motion Max Media Awards is open to Indian citizens and residents only. We plan to open international categories in future seasons.' },
      { id: 'g4', q: 'Can I submit in multiple categories?', a: 'Yes! You can submit the same work in multiple categories if it qualifies, or submit different works in different categories. Each submission requires a separate entry fee of ₹499.' },
    ],
  },
  {
    id: 'submission',
    label: 'Submission',
    faqs: [
      { id: 's1', q: 'What video formats and sizes are accepted?', a: 'We accept MP4, MOV, AVI, and MKV formats up to 2GB in size. Maximum video duration varies by category — typically 30 minutes for short films, 3 minutes for reels, and 8 minutes for music videos. Check the Categories page for specific limits.' },
      { id: 's2', q: 'Can I submit a previously released work?', a: 'Yes, works released within the last 24 months (after 1 January 2024) are eligible. The work must not have won a major national or international award previously.' },
      { id: 's3', q: 'What language should my submission be in?', a: 'Any Indian language is accepted. English subtitles are strongly recommended for non-English submissions to ensure judges can fully evaluate the work.' },
      { id: 's4', q: 'Can I edit or replace my submission after uploading?', a: 'Submissions can be replaced up to 48 hours after initial upload, provided the submission deadline has not passed. After that, no changes are permitted.' },
    ],
  },
  {
    id: 'judging',
    label: 'Judging',
    faqs: [
      { id: 'j1', q: 'How is judging conducted?', a: 'Each submission is evaluated by a panel of 3 independent judges on four criteria: Creativity & Concept, Technical Quality, Storytelling & Narrative, and Presentation & Impact. Final scores are averaged and rankings computed transparently.' },
      { id: 'j2', q: 'Who are the judges?', a: 'Our judging panel includes working film directors, cinematographers, editors, content strategists, and media industry veterans. Full judge profiles are published on our website before the judging period begins.' },
      { id: 'j3', q: 'Is the judging process anonymous?', a: 'Yes. Judges evaluate submissions without knowing the identity of the creator. Submissions are identified only by a unique submission ID during the judging process.' },
      { id: 'j4', q: 'Can I appeal a judging decision?', a: 'Judging decisions are final and cannot be appealed. However, all participants receive a detailed score breakdown and judge feedback within 30 days of results announcement.' },
    ],
  },
  {
    id: 'prizes',
    label: 'Prizes & Certificates',
    faqs: [
      { id: 'p1', q: 'When will results be announced?', a: 'Shortlisted entries will be notified by 25 October 2026. The Grand Ceremony and final results announcement is scheduled for 15 November 2026 in Chennai.' },
      { id: 'p2', q: 'How do I receive my prize money?', a: 'Prize money is transferred directly to the winner\'s registered bank account within 15 business days of the ceremony. Winners must submit KYC documents (PAN card + bank details) to claim their prize.' },
      { id: 'p3', q: 'How do I receive my certificate?', a: 'All participants receive a digital Participation Certificate via email within 7 days of the submission deadline. Winners receive both digital and physical Winner Certificates mailed to their registered address within 30 days of the ceremony.' },
      { id: 'p4', q: 'Are there prizes for all categories?', a: 'Yes. Every category has Gold, Silver, and Bronze awards with cash prizes. Additionally, there are special jury awards and the Grand Prix — Best of Show — which carries the highest prize of ₹1,00,000.' },
    ],
  },
  {
    id: 'payment',
    label: 'Payment & Refunds',
    faqs: [
      { id: 'pay1', q: 'What is the entry fee?', a: 'The entry fee is ₹499 per submission. Early bird pricing of ₹299 is available until 31 July 2026. Group discounts are available for institutions submitting 5 or more entries.' },
      { id: 'pay2', q: 'What payment methods are accepted?', a: 'We accept UPI, net banking, credit/debit cards, and all major digital wallets. Payments are processed securely through our payment gateway.' },
      { id: 'pay3', q: 'Can I get a refund if I withdraw my entry?', a: 'Entry fees are non-refundable once a submission has been uploaded and payment confirmed. Please review our Refund Policy for complete details.' },
    ],
  },
];

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState('general');
  const [openFaq, setOpenFaq] = useState<string | null>('g1');

  const currentFaqs = faqCategories?.find((c) => c?.id === activeCategory)?.faqs || [];

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(201,168,76,0.1) 0%, transparent 60%)' }} />
        <div className="max-w-screen-2xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)' }}>
            <HelpCircle size={14} className="text-primary" />
            <span className="text-xs font-semibold text-primary tracking-widest uppercase">Help Center</span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-extrabold text-foreground mb-6" style={{ letterSpacing: '-0.03em', lineHeight: 1.05 }}>
            Frequently Asked <span className="gradient-gold-text">Questions</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Everything you need to know about participating in Motion Max Media Awards.
          </p>
        </div>
      </section>

      {/* FAQ content */}
      <section className="py-10 px-6 lg:px-10 max-w-5xl mx-auto pb-24">
        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-10">
          {faqCategories?.map((cat) => (
            <button
              key={cat?.id}
              onClick={() => { setActiveCategory(cat?.id); setOpenFaq(null); }}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                activeCategory === cat?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'glass-card text-muted-foreground hover:text-primary hover:border-primary/40'
              }`}
            >
              {cat?.label}
            </button>
          ))}
        </div>

        {/* FAQ list */}
        <div className="space-y-3">
          {currentFaqs?.map((faq) => (
            <div
              key={faq?.id}
              className={`glass-card rounded-2xl overflow-hidden transition-all duration-300 ${openFaq === faq?.id ? 'border-primary/30 glow-gold-sm' : ''}`}
            >
              <button
                className="w-full flex items-center justify-between px-6 py-5 text-left"
                onClick={() => setOpenFaq(openFaq === faq?.id ? null : faq?.id)}
              >
                <span className="font-semibold text-sm text-foreground pr-4">{faq?.q}</span>
                <ChevronDown
                  size={18}
                  className={`text-primary flex-shrink-0 transition-transform duration-300 ${openFaq === faq?.id ? 'rotate-180' : ''}`}
                />
              </button>
              {openFaq === faq?.id && (
                <div className="px-6 pb-5">
                  <p className="text-sm text-muted-foreground leading-relaxed">{faq?.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Still have questions */}
        <div className="mt-14 glass-card rounded-3xl p-8 text-center" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}>
          <MessageCircle size={32} className="text-primary mx-auto mb-4" />
          <h3 className="text-xl font-bold text-foreground mb-2">Still have questions?</h3>
          <p className="text-sm text-muted-foreground mb-6">Our team is available Monday–Saturday, 10am–6pm IST.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/contact" className="btn-primary px-8 py-3 rounded-full font-bold inline-flex items-center gap-2 justify-center">
              Contact Us
            </Link>
            <a href="mailto:awards@motionmax.in" className="btn-outline-gold px-8 py-3 rounded-full font-semibold inline-flex items-center gap-2 justify-center">
              Email Support
            </a>
          </div>
        </div>
      </section>

      <HomeFooter />
    </div>
  );
}
